import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Tooltip from '@material-ui/core/Tooltip';
import Delete from '@material-ui/icons/Delete';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';

function desc(a, b, orderBy) {
  let thiz = a[orderBy];
  let that = b[orderBy];
  if (moment(thiz, 'DD.MM.YYYY HH:mm').isValid()) {
    thiz = moment(thiz, 'DD.MM.YYYY HH:mm');
    that = moment(that, 'DD.MM.YYYY HH:mm');
  }
  if (that < thiz) {
    return -1;
  }
  if (that > thiz) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy) {
  return order === 'desc'
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy);
}

const tableHeadStyles = theme => ({
  tableCell: {
    paddingLeft: 8,
    paddingRight: 8,
  },
});

class EnhancedTableHead extends React.Component {
  createSortHandler = property => event => {
    this.props.onRequestSort(event, property);
  };

  render() {
    const { order, orderBy, columnDefinition, classes } = this.props;

    return (
      <TableHead>
        <TableRow>
          {columnDefinition.map((header, index) => {
            return (
              <TableCell
                key={index}
                numeric={header.numeric}
                sortDirection={orderBy === header.id ? order : false}
                className={classes.tableCell}
              >
                <Tooltip title="Sort" placement="bottom-start" enterDelay={300}>
                  <TableSortLabel
                    active={orderBy === header.id}
                    direction={order}
                    onClick={this.createSortHandler(header.id)}
                  >
                    {header.label}
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
            );
          }, this)}
        </TableRow>
      </TableHead>
    );
  }
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  orderBy: PropTypes.string.isRequired,
};

const styles = theme => ({
  root: {
    width: '100%',
    marginTop: theme.spacing.unit * 3,
  },
  table: {
    minWidth: 400,
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  tableCell: {
    paddingLeft: 8,
    paddingRight: 8,
  },
});

class EnhancedTable extends React.Component {
  static defaultProps = {
    onRowClick: () => {},
    onCellClick: () => {},
    data: [],
    hover: true,
    order: 'asc',
    orderBy: '',
    page: 0,
    rowsPerPage: 5,
  };

  constructor(props) {
    super(props);
    const { order, orderBy, page, rowsPerPage } = props;
    this.state = {
      order,
      orderBy,
      page,
      rowsPerPage,
    };
  }

  handleRequestSort = (event, property) => {
    const orderBy = property;
    let order = 'desc';

    if (this.state.orderBy === property && this.state.order === 'desc') {
      order = 'asc';
    }

    this.setState({ order, orderBy });
  };

  handleChangePage = (event, page) => {
    this.setState({ page });
  };

  handleChangeRowsPerPage = event => {
    this.setState({ rowsPerPage: event.target.value });
  };

  render() {
    const {
      classes,
      columnDefinition,
      onRowClick,
      onCellClick,
      data,
      hover,
      onRowDelete,
      onPositionChange,
    } = this.props;
    const { order, orderBy, rowsPerPage, page } = this.state;
    const emptyRows =
      rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage);
    const WrappedTableHead = withStyles(tableHeadStyles)(EnhancedTableHead);

    return (
      <div className={classes.root}>
        <div className={classes.tableWrapper}>
          <Table className={classes.table} aria-labelledby="tableTitle">
            <WrappedTableHead
              columnDefinition={columnDefinition}
              order={order}
              orderBy={orderBy}
              onRequestSort={this.handleRequestSort}
            />
            <TableBody>
              {stableSort(data, getSorting(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((entry, index, list) => {
                  return (
                    <TableRow
                      key={entry.id}
                      hover={hover}
                      style={{ cursor: hover ? 'pointer' : 'inherit' }}
                      onClick={() => onRowClick(entry)}
                    >
                      {columnDefinition.map(
                        def =>
                          def.id === 'actions' ? (
                            <TableCell className={classes.tableCell} width={1}>
                              {onRowDelete ? (
                                <Delete onClick={() => onRowDelete(entry)} />
                              ) : null}
                              {onPositionChange ? (
                                <span>
                                  <KeyboardArrowUp
                                    onClick={() =>
                                      index === 0
                                        ? {}
                                        : onPositionChange(entry, 'up')
                                    }
                                  />
                                  <KeyboardArrowDown
                                    onClick={() =>
                                      index === list.length - 1
                                        ? {}
                                        : onPositionChange(entry, 'down')
                                    }
                                  />
                                </span>
                              ) : null}
                            </TableCell>
                          ) : (
                            <TableCell
                              key={def.id}
                              className={classes.tableCell}
                              onClick={() => onCellClick(entry)}
                            >
                              {typeof entry[def.id] === 'boolean'
                                ? entry[def.id]
                                  ? 'True'
                                  : 'False'
                                : entry[def.id]}
                            </TableCell>
                          )
                      )}
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 49 * emptyRows }}>
                  <TableCell colSpan={6} className={classes.tableCell} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            'aria-label': 'Previous Page',
          }}
          nextIconButtonProps={{
            'aria-label': 'Next Page',
          }}
          onChangePage={this.handleChangePage}
          onChangeRowsPerPage={this.handleChangeRowsPerPage}
        />
      </div>
    );
  }
}

EnhancedTable.propTypes = {
  classes: PropTypes.object.isRequired,
  columnDefinition: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
    })
  ),
  hover: PropTypes.bool,
  data: PropTypes.array,
  onRowClick: PropTypes.func,
  onCellClick: PropTypes.func,
  onRowDelete: PropTypes.func,
  onPositionChange: PropTypes.func,
};

export default withStyles(styles)(EnhancedTable);



// WEBPACK FOOTER //
// ./src/components/Table/OverviewTable.jsx